import type { TFunction } from "i18next";
import moment from "moment";
import {
  ContractDurationType,
  type AwardCriterionWeightDto,
  type ContractDurationDto,
  WeightType,
} from "../../../../../autogen/bff-api";
import { displayDate, type SupportedLocale } from "../../../../../common/formatting/displayDate";

export const deriveContractEndDate = (
  duration: ContractDurationDto | null | undefined,
): string | null => {
  if (!duration) return null;
  if (duration.type === ContractDurationType.FixedDates) return duration.endDate ?? null;
  if (duration.type === ContractDurationType.PeriodBased && duration.startDate && duration.periodMonths != null) {
    return moment(duration.startDate).add(duration.periodMonths, "months").format("YYYY-MM-DD");
  }
  return null;
};

export const formatContractDuration = (
  duration: ContractDurationDto | null | undefined,
  t: TFunction,
  locale?: SupportedLocale | null,
): string => {
  if (!duration) return "-";
  if (duration.type === ContractDurationType.OpenEnded) {
    return duration.startDate
      ? t("Running agreement from {{date}}", { date: displayDate(duration.startDate, locale) })
      : t("Running agreement");
  }
  if (duration.type === ContractDurationType.FixedDates) {
    return duration.startDate && duration.endDate
      ? `${displayDate(duration.startDate, locale)} - ${displayDate(duration.endDate, locale)}`
      : "-";
  }
  if (duration.type === ContractDurationType.PeriodBased) {
    if (duration.periodMonths == null) return "-";
    const months = t("month", { count: duration.periodMonths });
    if (!duration.startDate) return `${duration.periodMonths} ${months}`;
    const derivedEndDate = deriveContractEndDate(duration);
    if (derivedEndDate == null) {
      return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months}`;
    }
    return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months} (${t("ends {{date}}", {
      date: displayDate(derivedEndDate, locale),
    })})`;
  }
  return "-";
};

export const formatYesNo = (
  value: boolean | null | undefined,
  t: (key: string) => string,
): string | null => {
  if (value == null) return null;
  return value ? t("Yes") : t("No");
};

export const formatWeight = (
  weight: AwardCriterionWeightDto | null | undefined,
): string | null => {
  if (!weight) return null;
  if (weight.type === WeightType.Percentage) return `${weight.number}%`;
  return `${weight.number}`;
};
